import React, { useState, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, Typography, Button, IconButton, Alert, CircularProgress, TextField,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { AddressGroup } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  products: any[];
  addressGroups: AddressGroup[];
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function OnetimeModal({ open, onClose, products, addressGroups, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [addressId, setAddressId] = useState(addressGroups[0]?.addressId || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allVariants = useMemo(() => {
    const variants: Array<{ id: string; label: string; price: string }> = [];
    for (const product of products) {
      for (const variant of product.variants || []) {
        variants.push({
          id: String(variant.id),
          label: `${product.title} - ${variant.title} (£${variant.price})`,
          price: variant.price,
        });
      }
    }
    return variants;
  }, [products]);

  const handleConfirm = async () => {
    if (!selectedVariantId || !addressId) return;
    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) return;

    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/onetimes/add", {
        method: "POST",
        body: { addressId, variantId: selectedVariantId, quantity: qty },
      });
      if (!res.ok) throw new Error(res.error || "Failed to add one-time");
      onSuccess("One-time product added");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to add one-time");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Add One-time Product</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Address</InputLabel>
            <Select value={addressId} onChange={(e) => setAddressId(e.target.value)} label="Address">
              {addressGroups.map(g => (
                <MenuItem key={g.addressId} value={g.addressId}>
                  {g.address ? `${g.address.address1}, ${g.address.city}` : `ID: ${g.addressId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Product Variant</InputLabel>
            <Select value={selectedVariantId} onChange={(e) => setSelectedVariantId(e.target.value)} label="Product Variant">
              {allVariants.map(v => <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth type="number" label="Quantity" value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 1, max: 99 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !selectedVariantId || !addressId}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
